import { useEffect, useState, Fragment } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import BookletCanvas from "@/components/BookletCanvas";
import { SaveQuoteBar } from "@/components/SaveQuote";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, num } from "@/lib/format";
import { PricingPanel, useRushRates } from "@/components/Metric";
import ProductionTicket from "@/components/ProductionTicket";
import { MiscPicker } from "@/components/MiscPicker";
import { ServicesPicker } from "@/components/ServicesPicker";
import { MiscListTab } from "@/components/MiscListTab";
import { useRequote } from "@/lib/useRequote";
import { toast } from "sonner";
import { Calculator, Layers, FileStack, BookOpen, Tag, DollarSign, Trash2, Plus } from "lucide-react";
import { HelpHint } from "@/components/HelpHint";

const BINDINGS = [
  { v: "saddle", l: "Saddle Stitch" },
  { v: "spiral", l: "Spiral" },
  { v: "wireo", l: "Wire-O" },
  { v: "perfect", l: "Perfect Binding (Glue)" },
];
const PAGE_COUNTS = [8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48];
const STD_QTYS = [25, 50, 100, 250, 500, 1000, 2500, 5000];

const PAPER_GROUPS = [
  { key: "cardstock", label: "Cardstock", dot: "bg-indigo-500", text: "text-indigo-700", accentL: "border-l-indigo-400" },
  { key: "text", label: "Text", dot: "bg-amber-500", text: "text-amber-700", accentL: "border-l-amber-400" },
  { key: "copy", label: "Copy Paper", dot: "bg-slate-500", text: "text-slate-600", accentL: "border-l-slate-400" },
  { key: "other", label: "Other", dot: "bg-slate-400", text: "text-slate-500", accentL: "border-l-slate-300" },
];
const paperClass = (name = "") => {
  const t = String(name).toLowerCase();
  if (/(cover|cardstock|card stock|c2s|c1s|\d+\s*pt\b)/.test(t)) return "cardstock";
  if (/(copy|digital copy)/.test(t)) return "copy";
  if (/(text|book|bond|writing)/.test(t)) return "text";
  return "other";
};

function Metric({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`rounded-lg border p-4 ${accent ? "bg-[#2495D3] border-[#2495D3] text-white" : "bg-white border-slate-200"}`}>
      <div className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${accent ? "text-white/80" : "text-slate-500"}`}>
        <Icon size={13} /> {label}
      </div>
      <div className={`num text-2xl font-black mt-1.5 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 ${accent ? "text-white/70" : "text-slate-400"}`}>{sub}</div>}
    </div>
  );
}

export default function Booklet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const rush = useRushRates();
  const retailTaxF = 1 + (rush.gst + rush.pst) / 100;
  const wsTaxF = 1 + rush.gst / 100;

  const [stocks, setStocks] = useState([]);
  const [paperMats, setPaperMats] = useState([]);
  const [presets, setPresets] = useState([]);
  const [lamOptions, setLamOptions] = useState([]);
  const [foilOptions, setFoilOptions] = useState([]);
  const [focusQty, setFocusQty] = useState(100);
  const [orderNo, setOrderNo] = useState("");
  const [orderName, setOrderName] = useState("");
  const [prodTime, setProdTime] = useState("Standard");
  const [res, setRes] = useState(null);
  const [miscRows, setMiscRows] = useState([]);
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newPreset, setNewPreset] = useState({ name: "", width: "", height: "" });
  const [bindingOn, setBindingOn] = useState({ saddle: true, spiral: true, wireo: true, perfect: true });
  const availBindings = BINDINGS.filter((b) => bindingOn[b.v] !== false);

  const [f, setF] = useState({
    cover_stock_id: "", inside_stock_id: "", page_count: 8, binding: "saddle",
    presetId: "", width: 8.5, height: 11,
    laminate_cover: false, laminate_id: "", laminate_sides: "both",
    hot_foil_cover: false, foil_id: "", foil_sides: "both",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const loadPresets = () => api.get("/booklet-presets").then((r) => {
    const list = r.data || [];
    setPresets(list);
    const def = list.find((p) => Number(p.width) === 8.5 && Number(p.height) === 11) || list[0];
    if (def) setF((p) => (p.presetId ? p : { ...p, presetId: def.id, width: def.width, height: def.height }));
  }).catch(() => {});

  useEffect(() => {
    api.get("/paper-stocks").then((r) => {
      setStocks(r.data || []);
      const inside = (r.data || []).find((s) => /text|bond|book/i.test(s.name)) || (r.data || [])[1] || (r.data || [])[0];
      if (r.data?.[0]) setF((p) => ({ ...p, cover_stock_id: p.cover_stock_id || r.data[0].id, inside_stock_id: p.inside_stock_id || inside?.id }));
    }).catch(() => {});
    api.get("/paper-addons?type=laminate").then((r) => { setLamOptions(r.data || []); const d = (r.data || []).find((o) => o.is_default) || r.data?.[0]; if (d) setF((p) => ({ ...p, laminate_id: p.laminate_id || d.id })); }).catch(() => {});
    api.get("/paper-addons?type=hot_foil").then((r) => { setFoilOptions(r.data || []); const d = (r.data || []).find((o) => o.is_default) || r.data?.[0]; if (d) setF((p) => ({ ...p, foil_id: p.foil_id || d.id })); }).catch(() => {});
    loadPresets();
    api.get("/settings").then((r) => {
      const d = r.data || {};
      const bo = {
        saddle: d.binding_saddle_on !== false, spiral: d.binding_spiral_on !== false,
        wireo: d.binding_wireo_on !== false, perfect: d.binding_perfect_on !== false,
      };
      setBindingOn(bo);
      setF((p) => (bo[p.binding] ? p : { ...p, binding: (Object.keys(bo).find((k) => bo[k]) || "saddle") }));
    }).catch(() => {});
    if (isAdmin) api.get("/materials").then((r) => setPaperMats((r.data || []).filter((m) => (m.modules || []).includes("booklet") && (m.paper_type || "normal") === "normal"))).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const calc = async () => {
    if (!f.cover_stock_id || !f.inside_stock_id) return toast.error("Select cover and inside paper");
    setLoading(true);
    try {
      const body = {
        cover_stock_id: f.cover_stock_id, inside_stock_id: f.inside_stock_id,
        page_count: +f.page_count, quantity: +focusQty, width: +f.width, height: +f.height,
        binding: f.binding,
        laminate_cover: f.laminate_cover, laminate_id: f.laminate_cover ? f.laminate_id : null, laminate_sides: f.laminate_sides,
        hot_foil_cover: f.hot_foil_cover, foil_id: f.hot_foil_cover ? f.foil_id : null, foil_sides: f.foil_sides,
        misc: miscRows.filter((r) => r.material_id && +r.qty > 0).map((r) => ({ material_id: r.material_id, qty: +r.qty })),
        services,
      };
      const { data } = await api.post("/calc/booklet", body);
      setRes(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useRequote((rq) => { if (rq.f) setF((p) => ({ ...p, ...rq.f })); if (rq.focusQty) setFocusQty(rq.focusQty); }, calc,
    { moduleKey: "booklet", inputs: { f, focusQty }, hasResult: !!res });

  // Once a quote exists, auto-recalc (price + layout) whenever any input changes — no need to re-click Generate.
  useEffect(() => {
    if (!res) return;
    const t = setTimeout(() => { calc(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.cover_stock_id, f.inside_stock_id, f.width, f.height, f.page_count, f.binding,
      f.laminate_cover, f.laminate_id, f.laminate_sides, f.hot_foil_cover, f.foil_id, f.foil_sides]);

  const onPreset = (v) => {
    if (v === "custom") { setF((p) => ({ ...p, presetId: "custom" })); return; }
    const p = presets.find((x) => x.id === v);
    setF((s) => ({ ...s, presetId: v, width: p?.width ?? s.width, height: p?.height ?? s.height }));
  };
  const savePreset = async (name, width, height) => {
    if (!name || !Number(width) || !Number(height)) { toast.error("Enter name, width and height"); return; }
    try {
      const { data } = await api.post("/booklet-presets", { name, width: Number(width), height: Number(height) });
      toast.success("Preset saved");
      await loadPresets();
      setF((p) => ({ ...p, presetId: data.id, width: data.width, height: data.height }));
      setNewPreset({ name: "", width: "", height: "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const delPreset = async (id) => {
    try { await api.delete(`/booklet-presets/${id}`); toast.success("Preset deleted"); loadPresets(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };

  const focusRow = res?.rows?.find((r) => r.qty === focusQty) || res?.rows?.[0];
  const retailOf = (r) => r?.retail_total;
  const wholesaleOf = (r) => r?.wholesale_total;
  const isCustom = f.presetId === "custom";

  return (
    <div data-testid="booklet-page">
      <PageHeader title="Booklets" eyebrow="Live Pricing" subtitle="Imposition, cost comparison & instant pricing" />
      <div className="p-8">
        <Tabs defaultValue="calc">
          <TabsList className="rounded-full bg-slate-100 p-1">
            <TabsTrigger value="calc" data-testid="tab-calc" className="rounded-full">Calculator</TabsTrigger>
            {isAdmin && <TabsTrigger value="stocks" data-testid="tab-stocks" className="rounded-full">Paper Stock</TabsTrigger>}
            {isAdmin && <TabsTrigger value="presets" data-testid="tab-presets" className="rounded-full">Presets</TabsTrigger>}
            <TabsTrigger value="misc" data-testid="tab-misc" className="rounded-full">Miscellaneous</TabsTrigger>
          </TabsList>

          {/* ---------------- Calculator ---------------- */}
          <TabsContent value="calc" className="mt-6 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 bg-white border border-slate-200 rounded-xl p-6 h-fit">
              <h3 className="font-head font-bold mb-4">Quote Setup</h3>

              <Label className="text-xs flex items-center gap-1">Cover Page<HelpHint side="right">The paper used for the outer cover &mdash; usually a heavier cardstock than the inside pages.</HelpHint></Label>
              <Select value={f.cover_stock_id} onValueChange={(v) => set("cover_stock_id", v)}>
                <SelectTrigger data-testid="cover-select" className="rounded-lg mt-1 mb-4"><SelectValue placeholder="Cover paper" /></SelectTrigger>
                <SelectContent>{stocks.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>

              <Label className="text-xs flex items-center gap-1">Inside Pages<HelpHint side="right">The paper used for the interior pages (text stock) &mdash; lighter than the cover.</HelpHint></Label>
              <Select value={f.inside_stock_id} onValueChange={(v) => set("inside_stock_id", v)}>
                <SelectTrigger data-testid="inside-select" className="rounded-lg mt-1 mb-4"><SelectValue placeholder="Inside paper" /></SelectTrigger>
                <SelectContent>{stocks.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>

              <Label className="text-xs flex items-center gap-1">Size (folded)<HelpHint side="right">The finished size when the booklet is closed. The open spread is twice the width; the job is imposed on 12&times;18&quot;.</HelpHint></Label>
              <Select value={f.presetId} onValueChange={onPreset}>
                <SelectTrigger data-testid="size-select" className="rounded-lg mt-1 mb-2"><SelectValue placeholder="Choose size" /></SelectTrigger>
                <SelectContent>
                  {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  <SelectItem value="custom" data-testid="size-custom-option">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {isCustom && (
                <div className="grid grid-cols-2 gap-2 mb-2" data-testid="custom-size-inputs">
                  <div><Label className="text-[11px] text-slate-500">W folded (in)</Label>
                    <Input data-testid="custom-w" type="number" step="0.01" value={f.width} onChange={(e) => set("width", e.target.value)} className="rounded-lg mt-1" /></div>
                  <div><Label className="text-[11px] text-slate-500">H folded (in)</Label>
                    <Input data-testid="custom-h" type="number" step="0.01" value={f.height} onChange={(e) => set("height", e.target.value)} className="rounded-lg mt-1" /></div>
                  <div className="col-span-2">
                    <Button size="sm" variant="outline" data-testid="save-preset-inline" className="rounded-lg w-full"
                      onClick={() => savePreset(`${num(f.width)}"x${num(f.height)}" folded`, f.width, f.height)}>
                      <Plus size={14} className="mr-1" /> Save as preset
                    </Button>
                  </div>
                </div>
              )}
              <div className="text-[11px] text-slate-400 mb-4">Open spread = {num(f.width * 2)}×{num(f.height)}" · imposed on 12×18"</div>

              <Label className="text-xs flex items-center gap-1">Page Count<HelpHint side="right">Total pages including the cover. Saddle-stitch booklets must be a multiple of 4.</HelpHint></Label>
              <div className="flex flex-wrap gap-1.5 mt-1 mb-4">
                {PAGE_COUNTS.map((pc) => (
                  <button key={pc} data-testid={`page-count-${pc}`} onClick={() => set("page_count", pc)}
                    className={`num text-xs rounded-full px-3 py-1 border transition-colors ${+f.page_count === pc ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{pc}</button>
                ))}
              </div>

              <Label className="text-xs flex items-center gap-1">Binding<HelpHint side="right">How the booklet is held together: <b>Saddle Stitch</b> (staples), <b>Spiral / Wire-O</b> (coil), or <b>Perfect</b> (glued spine).</HelpHint></Label>
              <Select value={f.binding} onValueChange={(v) => set("binding", v)}>
                <SelectTrigger data-testid="binding-select" className="rounded-lg mt-1 mb-4"><SelectValue /></SelectTrigger>
                <SelectContent>{availBindings.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
              </Select>

              <Label className="text-xs flex items-center gap-1">Focus Quantity<HelpHint side="right">The quantity highlighted in the results. All tiers are still priced &mdash; this just picks the focus.</HelpHint></Label>
              <div className="flex flex-wrap gap-1.5 mt-1 mb-4">
                {STD_QTYS.map((q) => (
                  <button key={q} data-testid={`focus-qty-${q}`} onClick={() => setFocusQty(q)}
                    className={`num text-xs rounded-full px-3 py-1 border transition-colors ${focusQty === q ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{q}</button>
                ))}
              </div>

              {/* Laminate cover */}
              <div className="py-2 border-t border-slate-100 pt-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1">Laminate Cover<HelpHint side="left">Bonds a clear protective film to the cover. Flat rate per cover sheet &mdash; same price either way.</HelpHint></Label>
                  <Switch data-testid="laminate-switch" checked={f.laminate_cover} onCheckedChange={(v) => set("laminate_cover", v)} />
                </div>
                {f.laminate_cover && (
                  <div className="mt-2 space-y-2" data-testid="laminate-picker">
                    <Select value={f.laminate_id} onValueChange={(v) => set("laminate_id", v)}>
                      <SelectTrigger data-testid="laminate-select" className="rounded-lg h-9"><SelectValue placeholder="Choose laminate" /></SelectTrigger>
                      <SelectContent>{lamOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      {[["exterior", "Exterior only"], ["both", "Both sides"]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`lam-sides-${v}`} onClick={() => set("laminate_sides", v)}
                          className={`flex-1 text-xs rounded-lg border py-1.5 ${f.laminate_sides === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600"}`}>{l}</button>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400">Flat rate per cover sheet — same price either way.</p>
                    {lamOptions.length === 0 && <p className="text-[11px] text-slate-400">No laminates registered. Add one in Materials (Paper → Type: Laminate).</p>}
                  </div>
                )}
              </div>

              {/* Hot foil cover */}
              <div className="py-2 mb-4">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1">Hot Foil Cover<HelpHint side="left">Stamps metallic foil onto the cover with heat for a shiny accent. Priced per side.</HelpHint></Label>
                  <Switch data-testid="hotfoil-switch" checked={f.hot_foil_cover} onCheckedChange={(v) => set("hot_foil_cover", v)} />
                </div>
                {f.hot_foil_cover && (
                  <div className="mt-2 space-y-2" data-testid="foil-picker">
                    <Select value={f.foil_id} onValueChange={(v) => set("foil_id", v)}>
                      <SelectTrigger data-testid="foil-select" className="rounded-lg h-9"><SelectValue placeholder="Choose foil" /></SelectTrigger>
                      <SelectContent>{foilOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.foil_color ? ` · ${o.foil_color}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      {[["exterior", "Exterior only"], ["both", "Both sides"]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`foil-sides-${v}`} onClick={() => set("foil_sides", v)}
                          className={`flex-1 text-xs rounded-lg border py-1.5 ${f.foil_sides === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600"}`}>{l}</button>
                      ))}
                    </div>
                    {foilOptions.length === 0 && <p className="text-[11px] text-slate-400">No foils registered. Add one in Materials (Paper → Type: Hot Foil).</p>}
                  </div>
                )}
              </div>

              <MiscPicker module="booklet" toggle="switch" value={miscRows} onChange={setMiscRows} />
              <ServicesPicker module="booklet" toggle="switch" onChange={setServices} />

              <Button data-testid="calc-booklet-button" onClick={calc} disabled={loading} className="w-full mt-4 bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg h-11">
                <Calculator size={16} className="mr-2" />{loading ? "Calculating…" : "Generate Quote"}
              </Button>
              {res && focusRow && (
                <div className="mt-5"><ProductionTicket testid="booklet-ticket"
                  orderNo={orderNo} setOrderNo={setOrderNo} orderName={orderName} setOrderName={setOrderName}
                  machine="Booklet line"
                  material={res?.cover?.name}
                  rows={[
                    { label: "Pages", value: f.page_count },
                    { label: "Binding", value: f.binding },
                    { label: "Sheets / booklet", value: 1 + (res?.inside_sheets_per_book || 0) },
                    { label: "Size", value: `${f.width}"×${f.height}"` },
                  ]}
                  total={focusQty} totalLabel="Total booklets" prodTime={prodTime} /></div>
              )}
            </div>

            {/* Results */}
            <div className="lg:col-span-8">
              {!res || !focusRow ? (
                <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400">Configure a booklet and generate an instant quote with volume pricing.</div>
              ) : (
                <div className="space-y-6" data-testid="booklet-results">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(() => {
                      const rTot = retailOf(focusRow);
                      const wTot = wholesaleOf(focusRow);
                      const sheets = (focusRow.cover_sheets || 0) + (focusRow.inside_sheets || 0);
                      const baseCost = focusRow.base_cost || 0;
                      const qty = focusRow.qty || focusQty;
                      const costPiece = qty ? baseCost / qty : 0;
                      const per = (v) => (sheets && v != null ? v / sheets : 0);
                      const primary = rTot != null ? rTot : wTot;
                      const primaryTaxF = rTot != null ? retailTaxF : wsTaxF;
                      return (<>
                        <Metric icon={Layers} label="Sheets / Booklet" value={1 + (res.inside_sheets_per_book || 0)} sub={`1 cover + ${res.inside_sheets_per_book || 0} inside`} />
                        <Metric icon={FileStack} label={`Cover / Inside · ${focusQty}`} value={`${focusRow.cover_sheets} / ${focusRow.inside_sheets}`} sub="press sheets total" />
                        <Metric icon={BookOpen} label="Pages" value={res.page_count} sub={BINDINGS.find((b) => b.v === res.binding)?.l} />
                        <Metric icon={DollarSign} label="Cost / Piece" value={money(costPiece)} sub={`${money(baseCost)} job cost`} />
                        {rTot != null
                          ? <Metric icon={Tag} label={`Retail · ${focusQty}`} value={money(rTot)} sub={wTot != null ? `WS ${money(wTot)}` : null} accent />
                          : <Metric icon={Tag} label={`Wholesale · ${focusQty}`} value={money(wTot)} accent />}
                        <Metric icon={FileStack} label="Price / Sheet" value={money(per(primary))} sub={rTot != null && wTot != null ? `WS ${money(per(wTot))}` : null} />
                        <Metric icon={Tag} label="With Tax" value={money(primary * primaryTaxF)} sub={rTot != null && wTot != null ? `WS ${money(wTot * wsTaxF)}` : null} />
                      </>);
                    })()}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Booklet Layout · 12×18"</div>
                      <BookletCanvas openW={res.open_w} openH={res.open_h} foldedW={res.folded_w} foldedH={res.folded_h} nUp={res.n_up} binding={res.binding} />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col">
                      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Selected Papers</div>
                      <div className="font-head font-bold text-lg mt-1">{res.cover?.name}</div>
                      <div className="text-[11px] text-slate-400 -mt-0.5">inside: {res.inside?.name}</div>
                      <div className="mt-auto pt-4">
                        <PricingPanel r={{
                          base_cost: focusRow.base_cost, retail_total: focusRow.retail_total,
                          wholesale_total: focusRow.wholesale_total, unit_price: focusRow.unit_price,
                          wholesale_unit: focusRow.wholesale_unit, qty: focusRow.qty,
                          lamination_retail: focusRow.lamination_retail, lamination_wholesale: focusRow.lamination_wholesale,
                          foil_retail: focusRow.foil_retail, foil_wholesale: focusRow.foil_wholesale,
                        }} onRush={(x) => setProdTime((x.label || "Standard").replace(/\s*\+\d+%$/, ""))} />
                        {focusRow.volume_discount_pct > 0 && (
                          <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-emerald-600" data-testid="booklet-volume-discount">Volume discount · {focusRow.volume_discount_pct}% off @ {focusQty} pc</div>
                        )}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <SaveQuoteBar module="Booklet" title={`Booklet ${res.cover?.name || ""} · ${res.page_count}pp · ${focusQty}`} inputs={{ f, focusQty }} summary={{ ...res, focus_qty: focusQty, row: focusRow }} />
                      </div>
                    </div>
                  </div>

                  {/* Volume pricing table */}
                  <div data-testid="booklet-volume-table">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Volume Pricing · {res.cover?.name}</div>
                    <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                      <table className="w-full min-w-[720px] text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                            <th className="text-left px-4 py-2">Qty</th>
                            <th className="text-right px-4 py-2">Discount</th>
                            {retailOf(focusRow) != null && <th className="text-right px-4 py-2">Retail / unit</th>}
                            {retailOf(focusRow) != null && <th className="text-right px-4 py-2">Retail total</th>}
                            {retailOf(focusRow) != null && <th className="text-right px-4 py-2">Retail +tax</th>}
                            {wholesaleOf(focusRow) != null && <th className="text-right px-4 py-2">WS / unit</th>}
                            {wholesaleOf(focusRow) != null && <th className="text-right px-4 py-2">WS total</th>}
                            {wholesaleOf(focusRow) != null && <th className="text-right px-4 py-2">WS +tax</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {res.rows.map((row) => (
                            <tr key={row.qty} data-testid="booklet-volume-row" onClick={() => setFocusQty(row.qty)}
                              className={`border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${row.qty === focusQty ? "bg-[#2495D3]/5" : ""}`}>
                              <td className="px-4 py-2 num font-semibold">{row.qty === focusQty && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2495D3] mr-2 align-middle" />}{row.qty}</td>
                              <td className="px-4 py-2 text-right num">{(row.volume_discount_pct || 0) > 0 ? <span className="text-emerald-600">−{row.volume_discount_pct}%</span> : <span className="text-slate-300">—</span>}</td>
                              {retailOf(row) != null && <td className="px-4 py-2 text-right num text-slate-600">{money(row.unit_price)}</td>}
                              {retailOf(row) != null && <td className="px-4 py-2 text-right num font-semibold text-[#2495D3]">{money(retailOf(row))}</td>}
                              {retailOf(row) != null && <td className="px-4 py-2 text-right num text-[#2495D3]">{money(retailOf(row) * retailTaxF)}</td>}
                              {wholesaleOf(row) != null && <td className="px-4 py-2 text-right num text-slate-500">{money(row.wholesale_unit)}</td>}
                              {wholesaleOf(row) != null && <td className="px-4 py-2 text-right num text-slate-600">{money(wholesaleOf(row))}</td>}
                              {wholesaleOf(row) != null && <td className="px-4 py-2 text-right num text-slate-600">{money(wholesaleOf(row) * wsTaxF)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">Tap a row to set it as your focus quantity. +tax: Retail incl. GST {rush.gst}% + PST {rush.pst}%; WS incl. GST {rush.gst}% only.</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ---------------- Paper Stock ---------------- */}
          <TabsContent value="stocks" className="mt-6">
            {isAdmin && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-500">Reference view · paper stocks read from the central Materials DB (prices honor any overrides).</p>
                  <a href="/materials" className="text-xs text-[#2495D3] hover:underline" data-testid="manage-materials-link">Manage in Materials →</a>
                </div>
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        <th className="text-left px-4 py-2.5">Paper</th>
                        <th className="text-right px-4 py-2.5">Unit cost</th>
                        <th className="text-right px-4 py-2.5">Finish cost</th>
                        <th className="text-right px-4 py-2.5">Retail</th>
                        <th className="text-right px-4 py-2.5">Wholesale</th>
                        <th className="text-center px-4 py-2.5">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...paperMats].sort((a, b) => (PAPER_GROUPS.findIndex((g) => g.key === paperClass(a.name))) - (PAPER_GROUPS.findIndex((g) => g.key === paperClass(b.name)))).map((m, idx, arr) => {
                        const g = PAPER_GROUPS.find((x) => x.key === paperClass(m.name));
                        const showHeader = idx === 0 || paperClass(arr[idx - 1].name) !== g.key;
                        return (
                          <Fragment key={m.id}>
                            {showHeader && (
                              <tr data-testid={`stock-group-${g.key}`}><td colSpan={6} className={`px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest ${g.text} bg-slate-50/70`}><span className={`inline-block w-2 h-2 rounded-full ${g.dot} mr-2 align-middle`}></span>{g.label}</td></tr>
                            )}
                            <tr data-testid="paper-stock-row" className={`border-b border-slate-100 hover:bg-slate-50 border-l-4 ${g.accentL}`}>
                              <td className="px-4 py-2.5">
                                <div className="font-medium flex items-center gap-2">{m.name}
                                  {(m.default_modules || []).includes("booklet") && <span className="bg-amber-100 text-amber-700 text-[10px] rounded px-1.5 py-0.5">DEFAULT</span>}
                                </div>
                                <div className="text-[11px] text-slate-400 font-mono">{m.size || "—"}</div>
                              </td>
                              <td className="px-4 py-2.5 text-right num">{money(m.unit_cost)}</td>
                              <td className="px-4 py-2.5 text-right num font-semibold">{money(m.finish_cost)}</td>
                              <td className="px-4 py-2.5 text-right num text-[#2495D3]">{money(m.selling_price)}</td>
                              <td className="px-4 py-2.5 text-right num text-slate-600">{money(m.wholesale_price)}</td>
                              <td className="px-4 py-2.5 text-center num">{m.stock_qty}</td>
                            </tr>
                          </Fragment>
                        );
                      })}
                      {paperMats.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No paper materials assigned to this module. Add one in Materials.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ---------------- Presets ---------------- */}
          <TabsContent value="presets" className="mt-6">
            {isAdmin && (
              <div className="max-w-2xl">
                <p className="text-xs text-slate-500 mb-3">Booklet size presets (folded). Used in the calculator Size dropdown.</p>
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        <th className="text-left px-4 py-2.5">Preset</th>
                        <th className="text-right px-4 py-2.5">Folded</th>
                        <th className="text-right px-4 py-2.5">Open spread</th>
                        <th className="text-center px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {presets.map((p) => (
                        <tr key={p.id} data-testid="preset-row" className="border-b border-slate-100 last:border-0">
                          <td className="px-4 py-2.5 font-medium">{p.name}</td>
                          <td className="px-4 py-2.5 text-right num">{num(p.width)}×{num(p.height)}"</td>
                          <td className="px-4 py-2.5 text-right num text-slate-500">{num(p.width * 2)}×{num(p.height)}"</td>
                          <td className="px-4 py-2.5 text-center">
                            <button data-testid={`preset-delete-${p.id}`} onClick={() => delPreset(p.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
                          </td>
                        </tr>
                      ))}
                      {presets.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No presets yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="border border-slate-200 rounded-xl bg-white p-4">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Add preset</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                    <div className="col-span-2"><Label className="text-[11px] text-slate-500">Name</Label>
                      <Input data-testid="new-preset-name" value={newPreset.name} onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })} className="rounded-lg mt-1" placeholder='e.g. 6"x9" folded' /></div>
                    <div><Label className="text-[11px] text-slate-500">W folded</Label>
                      <Input data-testid="new-preset-w" type="number" step="0.01" value={newPreset.width} onChange={(e) => setNewPreset({ ...newPreset, width: e.target.value })} className="rounded-lg mt-1" /></div>
                    <div><Label className="text-[11px] text-slate-500">H folded</Label>
                      <Input data-testid="new-preset-h" type="number" step="0.01" value={newPreset.height} onChange={(e) => setNewPreset({ ...newPreset, height: e.target.value })} className="rounded-lg mt-1" /></div>
                  </div>
                  <Button data-testid="add-preset-btn" onClick={() => savePreset(newPreset.name, newPreset.width, newPreset.height)} className="mt-3 bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg">
                    <Plus size={15} className="mr-1" /> Add preset
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="misc" className="mt-6">
            <MiscListTab module="booklet" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
